/**
 * TopicFilter — a TopicSelector that is gated on a subject being chosen.
 *
 * Topics are subject-scoped: a topic only means something inside one subject,
 * so picking one before a subject is not a valid state. Rather than hide the
 * control until a subject exists (which makes it appear mid-row and shifts the
 * filter bar), it stays visible and blocks interaction, explaining why the
 * first time the user reaches for it.
 *
 * This behaviour was previously hand-rolled per page — the same `blocked` +
 * `onBlockedInteraction` + Modal trio duplicated wherever a topic filter sat
 * next to a subject filter. Use this component instead so the wording and the
 * rule stay identical everywhere.
 */

import { useState } from 'react';
import { TopicSelector } from './TopicSelector';
import type { ModuleItem } from '../reference-data/types';
import { BasicInfoAlert } from './BasicInfoAlert';

export interface TopicFilterProps {
  /** The currently selected subject. Empty/undefined ⇒ the control is blocked. */
  subject?: string;
  /** Topics for `subject` only — never a cross-subject list. */
  modules: ModuleItem[];
  value: string;
  onChange: (moduleId: string) => void;
  /** Shown when a subject IS selected. Defaults to "All topics". */
  placeholder?: string;
  /** Shown when no subject is selected yet. */
  blockedPlaceholder?: string;
  className?: string;
  disabled?: boolean;
}

export function TopicFilter({
  subject,
  modules,
  value,
  onChange,
  placeholder = 'All topics',
  blockedPlaceholder = 'Select topic',
  className,
  disabled,
}: TopicFilterProps) {
  const [showPickSubject, setShowPickSubject] = useState(false);
  const blocked = !subject;

  return (
    <>
      <TopicSelector
        modules={blocked ? [] : modules}
        value={value}
        onChange={onChange}
        placeholder={blocked ? blockedPlaceholder : placeholder}
        blocked={blocked}
        onBlockedInteraction={() => setShowPickSubject(true)}
        disabled={disabled}
        className={className}
      />
      <BasicInfoAlert
        isOpen={showPickSubject}
        message="Please select a subject before choosing a topic."
        onClose={() => setShowPickSubject(false)}
      />
    </>
  );
}
